import { FlightSegment, OnlyAviaPackage } from '@appTypes/api.types';

/** "6h 00m" (flights-api's segment duration) -> minutes; null when it cannot be read. */
export function parseFlightDuration(duration?: string | null): number | null {
  let minutes: number | null = null;
  for (const token of (duration ?? '').toLowerCase().split(' ')) {
    const value = Number(token.slice(0, -1));
    if (token.length < 2 || !Number.isInteger(value)) continue;
    if (token.endsWith('h')) minutes = (minutes ?? 0) + value * 60;
    else if (token.endsWith('m')) minutes = (minutes ?? 0) + value;
  }
  return minutes;
}

/**
 * Door-to-door minutes of one direction: every segment's flight time plus the layovers between them. A layover is the
 * next departure minus the previous arrival - both local times of the same airport, so no time zone is involved (the
 * flight times themselves are not, which is why the segments' own durations are used for them). Null when a duration
 * or a date is missing.
 */
export function journeyMinutes(segments?: FlightSegment[] | null): number | null {
  if (!segments?.length) return null;
  let total = 0;
  for (const [index, segment] of segments.entries()) {
    const flight = parseFlightDuration(segment.flightDuration);
    if (flight === null) return null;
    total += flight;
    if (index > 0) {
      const arrived = new Date(segments[index - 1].arrivalFlightDate ?? NaN).getTime();
      const departs = new Date(segment.departureFlightDate ?? NaN).getTime();
      if (Number.isNaN(arrived) || Number.isNaN(departs) || departs < arrived) return null;
      total += (departs - arrived) / 60_000;
    }
  }
  return total;
}

/**
 * results-view-toggle.md R-RT2 (docs#442): a segment's local date AND time as one number for the Departure / Arrival sort -
 * `2027-01-16T13:20:00.000` -> its milliseconds. Null when the value cannot be read (the row then goes last, as every
 * row without a value). Every value of one answer is a local time written the same way, so no time zone is involved.
 * Typed `Date` in api.types, but the JSON answer carries the ISO text - both are read.
 */
export function flightMoment(value?: Date | string | null): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * flight-fare-picker README "an arrival on the next day shows its date" (docs#447 point 2): how many calendar days after
 * the direction's first departure its last segment lands (local dates of the two airports) - 0 the same day, 1 the next
 * day ... Null when a date is missing or cannot be read.
 */
export function arrivalDayOffset(segments?: FlightSegment[] | null): number | null {
  if (!segments?.length) return null;
  const day = (value?: Date | string | null) => {
    const ms = flightMoment(value);
    if (ms === null) return null;
    const d = new Date(ms);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  };
  const departs = day(segments[0].departureFlightDate);
  const lands = day(segments.at(-1)?.arrivalFlightDate);
  return departs === null || lands === null ? null : lands - departs;
}

/**
 * website-web#11: the flight-duration filter. A package passes when each of its directions takes at most `maxHours`.
 * A direction whose journey time cannot be read is not held against the package - it stays in the list.
 */
export function withinJourneyTime(item: OnlyAviaPackage, maxHours: number | null | undefined): boolean {
  if (!maxHours) return true;
  return [item.flight?.departureSegments, item.flight?.returnSegments]
    .filter((segments) => segments?.length)
    .every((segments) => {
      const minutes = journeyMinutes(segments);
      return minutes === null || minutes <= maxHours * 60;
    });
}
